/**
 * O ROTEIRIZADOR ENTENDEU O QUE FOI ESCRITO?
 *
 * ── O PONTO (`T4.2`) ─────────────────────────────────────────────────────────────────────────
 *
 * Ele, na letra: *"eu botei aleatoriamente aqui eu escrevi várias letras, vamos ver o que ele vai
 * entender (...) então é possível que nada aconteça não só deu feio de tufete então não deu certo
 * isso aí a parte do escreva história toda não funcionou"*.
 *
 * O próprio ponto registra a ressalva: **ele desqualificou o próprio teste** — a entrada foi
 * aleatória de propósito. Mas ele diz "não funcionou", e a razão é boa: o produto **não avisou
 * nada**.
 *
 * ── O QUE FOI MEDIDO, EM 13/09, CONTRA PRODUÇÃO ──────────────────────────────────────────────
 *
 * `POST /roteirizar` com `"bababababababa asdkjh qweqwe zxczxc bbbb aaaa"` devolve **200**, com um
 * documento de uma página e três quadros — e **nenhum balão, nenhuma narração**. O título vira a
 * própria sequência de letras.
 *
 * A mesma rota com `"Ana perdeu o caderno na escola. Ela procurou na sala e no pátio..."` devolve
 * quadros **com** `Narração 1` dentro.
 *
 * Então a diferença entre "entendeu" e "não entendeu" está no dado, é objetiva, e a tela a
 * ignorava: em ambos os casos ela dizia `pronto` e mostrava o resultado como se fosse um
 * quadrinho. No caso ruim, o que ela mostrava eram três retângulos vazios.
 *
 * ── POR QUE A REGRA É ESTA, E NÃO "ADIVINHAR SE O TEXTO FAZ SENTIDO" ─────────────────────────
 *
 * Não é papel desta função julgar a escrita de uma criança. Ortografia ruim, frase torta e
 * invenção de palavra **são** história, e recusá-las seria pior que o defeito.
 *
 * O que ela mede é outra coisa, e é sobre o PRODUTO e não sobre o texto: **o roteirizador
 * conseguiu extrair alguma fala ou narração?** Se não conseguiu, o que ele devolveu é uma folha
 * em branco com moldura — e isso a criança precisa ouvir com todas as letras, em vez de receber
 * como se fosse o quadrinho dela.
 *
 * ── 27/09 · ESTA GUARDA JÁ NÃO ALCANÇA O CASO DELE, E O MOTIVO NÃO É ELA ─────────────────────
 *
 * Medido em produção, duas entradas, esperando o FIM da geração e não a primeira imagem:
 *
 *     "asdkjh askjdh askjdh qwoieu zxcmnb poiuyt lkjhgf mnbvcx"  →  1 cena, 1 folha, com narração
 *     "x"                                                        →  1 cena, 1 folha, com narração
 *
 * Nos dois casos `quantasFalas` devolve mais que zero, então esta função diz "entendeu" e a tela
 * mostra o quadrinho. **A premissa da regra deixou de valer**, e não por ela ter mudado: em 13/09 o
 * backend devolvia três quadros VAZIOS para letra aleatória, e desde o `BL-2.16` ele separa as cenas
 * por LLM com **queda para a quebra por regra** — e a quebra por regra transforma qualquer linha numa
 * narração. Hoje, com o crédito do provedor esgotado (HTTP 402), a queda é o caminho normal.
 *
 * O QUE NÃO SE FAZ AQUI, e continua valendo: julgar o texto. Distinguir "asdkjh" de uma história de
 * uma linha exige entender as palavras, e uma história de uma linha é história — "A Bia perdeu o
 * caderno." tem o mesmo tamanho e a mesma forma. Quem sabe fazer essa distinção é o LLM, e é por isso
 * que o `T4.2` ficou parado numa decisão de dinheiro e não numa de código.
 *
 * O QUE O PRODUTO JÁ DIZ, e é verdade: quando a queda acontece, a criança lê *"Separei as cenas do
 * jeito simples desta vez. Se a divisão não ficou boa, escreva uma cena por linha e monte de novo."* —
 * medido na mesma passada. Não é "não achei história", mas também não é o silêncio do achado.
 *
 * ESTA FUNÇÃO FICA. Ela é a resposta certa para o caso de zero falas, que voltará a existir no dia em
 * que o backend deixar de cair na quebra por regra — e um guarda retirado porque "não dispara hoje" é
 * um guarda que ninguém repõe.
 */
import { DocumentoHQ } from "../../api/documento.types";

/** Quantos balões e textos o documento inteiro tem. É o sinal de que houve roteiro. */
export function quantasFalas(documento: DocumentoHQ): number {
  let total = 0;
  for (const pagina of documento.paginas ?? []) {
    for (const camada of pagina.camadas ?? []) {
      for (const elemento of camada.elementos ?? []) {
        if (elemento.tipo === "balao" || elemento.tipo === "texto") total++;
      }
    }
  }
  return total;
}

/**
 * O roteirizador entendeu a história?
 *
 * Entendeu = produziu ao menos uma fala ou narração. Zero falas significa que ele devolveu
 * moldura vazia, e é o caso do `T4.2`.
 */
export function entendeuAHistoria(documento: DocumentoHQ): boolean {
  return quantasFalas(documento) > 0;
}

/**
 * O que a criança lê quando não deu.
 *
 * Três coisas, nesta ordem, e nenhuma delas é "erro": **o que aconteceu**, **por que** e **o que
 * fazer agora**. E não culpa quem escreveu — o texto dela continua no campo, e a frase não sugere
 * que ela escreveu errado, porque muitas vezes não escreveu.
 */
export const NAO_ENTENDI =
  "Não consegui achar uma história aqui dentro — saíram quadros, mas sem nenhuma fala ou " +
  "narração. Isso costuma acontecer quando o texto é curto demais ou não conta o que acontece. " +
  "Tente escrever em frases: quem é o personagem, onde ele está e o que acontece com ele. O que " +
  "você escreveu continua aí no campo.";
